import React, { useCallback, useState } from 'react';
import ConfirmationModal from 'components/Shared/ConfirmationModal/ConfirmationModal';
import { ConfirmationDialogProps } from 'components/Shared/shared.types';

// Use this hook to render a confirmation modal to block users navigation when there are unsaved changes in the current page
// e.g., currently this is used in the `UnsavedPrompt` component which blocks the navigation in Zones & Modules and Merchant Portal
const useConfirmNavigationModal = (props: ConfirmationDialogProps) => {
  const { title, description, icon, theme, primaryAction, secondaryAction, confirmationInput, className } = props;
  const [promise, setPromise] = useState<any>(null);

  const showConfirmNavigationModal = useCallback(
    async () =>
      new Promise((resolve) => {
        setPromise({ resolve });
      }),
    [],
  );

  const handleClose = () => {
    setPromise(null);
  };

  const handleCancel = useCallback(() => {
    promise?.resolve(false);
    primaryAction.onClickHandler?.();
    handleClose();
  }, [promise, primaryAction]);

  const handleConfirm = useCallback(() => {
    promise?.resolve(true);
    secondaryAction.onClickHandler?.();
    handleClose();
  }, [promise, secondaryAction]);

  const ConfirmNavigationModal = useCallback(() => {
    return (
      <ConfirmationModal
        isOpen={promise !== null}
        closeModal={handleCancel}
        theme={theme}
        icon={icon}
        title={title}
        description={description}
        primaryAction={{
          value: primaryAction.value ?? 'Cancel',
          onClickHandler: handleCancel,
        }}
        secondaryAction={{
          value: secondaryAction.value ?? 'Continue',
          onClickHandler: handleConfirm,
        }}
        confirmationInput={confirmationInput}
        className={className}
      />
    );
  }, [
    icon,
    theme,
    title,
    description,
    primaryAction.value,
    secondaryAction.value,
    confirmationInput,
    className,
    promise,
    handleCancel,
    handleConfirm,
  ]);

  return [ConfirmNavigationModal, showConfirmNavigationModal] as const;
};

export default useConfirmNavigationModal;
